import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { updateSessionEmail } from '../../../components/redux/slices/onboardingSlice';
import { useSignup } from '../../../components/redux/hooks/useSignup';
import { useOnboarding } from '../../../components/redux/hooks/useOnboarding';
import { usePostHog, POSTHOG_EVENTS } from '../../../components/analytics/PostHogProvider';
import api from '../../../services/api';


const LoginModal = ({ isOpen, onClose, onLoginSuccess }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { updateEmail, updateEmailError, updateIsEmailValid, email, emailError, isEmailValid } = useSignup();
  const { sessionId, isUpdatingSession, updateSessionError, sessionData, setAuthenticationState, updateSessionIdState } = useOnboarding();
  const { captureEvent, identifyUser } = usePostHog();
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [isPolling, setIsPolling] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState('');
  const pollingIntervalRef = useRef(null);
  const pollingTimeoutRef = useRef(null);
  const consecutiveErrorsRef = useRef(0);
  const MAX_CONSECUTIVE_ERRORS = 3;
  const currentSessionIdRef = useRef(sessionId);

  // Update ref when sessionId changes
  useEffect(() => {
    currentSessionIdRef.current = sessionId;
  }, [sessionId]);

  // Track login modal opened
  useEffect(() => {
    if (isOpen) {
      captureEvent('login_modal_opened', { sessionId });
    }
  }, [isOpen, captureEvent, sessionId]);

  const handleEmailChange = (e) => {
    const value = e.target.value;
    updateEmail(value);
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (value && !emailRegex.test(value)) {
      updateEmailError('Please enter a valid email address');
      updateIsEmailValid(false);
    } else {
      updateEmailError('');
      updateIsEmailValid(true);
    }
  };

  // Initiate verification - POST call made once
  const initiateVerification = async (email) => {
    const currentSessionId = currentSessionIdRef.current;
    if (!currentSessionId) {
      console.error('No session ID available for verification');
      return false;
    }

    try {
      const response = await api.post(`/onboarding/sessions/${currentSessionId}/verification/`, {
        email
      });
      console.log('Verification initiated:', response.data);
      
      // Update session ID if returned from server (handles returning user case)
      if (response.data.session_id && response.data.session_id !== currentSessionId) {
        console.log('Updating session ID from verification response:', response.data.session_id);
        updateSessionIdState(response.data.session_id);
        currentSessionIdRef.current = response.data.session_id;
      }
      
      captureEvent('verification_email_sent', { sessionId: response.data.session_id || currentSessionId, email });
      return true;
    } catch (error) {
      console.error('Error initiating verification:', error);
      captureEvent('verification_email_failed', { sessionId: currentSessionIdRef.current, email, error: error.message });
      setLoginError('Failed to initiate verification. Please try again.');
      return false;
    }
  };

  // Check verification status - GET call for polling
  const checkVerificationStatus = async () => {
    const currentSessionId = currentSessionIdRef.current;
    if (!currentSessionId) {
      console.error('No session ID available for verification check');
      stopPolling();
      return false;
    }

    try {
      console.log('Checking verification status with session ID:', currentSessionId);
      const response = await api.get(`/onboarding/sessions/${currentSessionId}/verification/`);

      if (response.data.status === 'verified') {
        // Email verified, we have tokens
        console.log('Email verified! Tokens received:', response.data.tokens);
        
        // Store tokens in localStorage
        localStorage.setItem('access_token', response.data.tokens.access_token);
        localStorage.setItem('refresh_token', response.data.tokens.refresh_token);
        
        // Set authentication state in Redux with user data from sessionData
        setAuthenticationState({
          user: {
            email: sessionData?.email || email,
            firstName: sessionData?.first_name,
            lastName: sessionData?.last_name,
            emailVerified: true,
          },
          tokens: response.data.tokens,
        });
        
        // Track successful login and identify user
        captureEvent(POSTHOG_EVENTS.USER_LOGIN, { 
          sessionId: currentSessionId, 
          email: sessionData?.email || email,
          method: 'email_verification',
        });
        identifyUser(currentSessionId, {
          email: sessionData?.email || email,
          first_name: sessionData?.first_name,
          last_name: sessionData?.last_name,
          email_verified: true,
          login_timestamp: new Date().toISOString(),
        });
        
        // Stop polling
        stopPolling();
        
        // Close modal
        onClose();
        
        // Call onLoginSuccess callback if provided, otherwise navigate to dashboard
        if (onLoginSuccess) {
          onLoginSuccess();
        } else {
          navigate('/onboarding/dashboard');
        }
        
        return true;
      } else if (response.data.status === 'pending') {
        // Clear any previous errors when status is pending
        setLoginError('');
        setVerificationStatus('Waiting for verification...');
        return false;
      } else {
        // Error response from API
        const errorMsg = response.data.error || response.data.details || 'Verification check failed';
        console.error('Verification check returned error:', response.data);
        // Stop polling and show error
        stopPolling();
        setVerificationStatus('');
        setLoginError(`Verification failed: ${errorMsg}`);
        return false;
      }
    } catch (error) {
      consecutiveErrorsRef.current++;
      console.error('Error checking verification status:', error);
      
      const errorMsg = error.response?.data?.error || error.response?.data?.details || error.message;
      
      // Show error to user after first failure
      if (consecutiveErrorsRef.current === 1) {
        setLoginError('');
        setVerificationStatus(`Network error: ${errorMsg}. Retrying...`);
      } else if (consecutiveErrorsRef.current >= MAX_CONSECUTIVE_ERRORS) {
        // Stop polling after too many consecutive errors
        stopPolling();
        setVerificationStatus('');
        setLoginError(`Failed after ${MAX_CONSECUTIVE_ERRORS} attempts: ${errorMsg}. Please try again.`);
      } else {
        setLoginError('');
        setVerificationStatus(`Network error (${consecutiveErrorsRef.current}/${MAX_CONSECUTIVE_ERRORS}): ${errorMsg}. Retrying...`);
      }
      return false;
    }
  };

  const startPolling = () => {
    // Clear any previous errors when starting polling
    setLoginError('');
    setIsPolling(true);
    setVerificationStatus('Checking verification status...');
    
    // Start polling every 5 seconds
    pollingIntervalRef.current = setInterval(() => {
      checkVerificationStatus();
    }, 5000);
    
    // Stop polling after 5 minutes (300 seconds)
    pollingTimeoutRef.current = setTimeout(() => {
      stopPolling();
      captureEvent('verification_timeout', { sessionId, email });
      setLoginError('Verification timed out. Please try again.');
    }, 300000);
  };

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
    consecutiveErrorsRef.current = 0;
    setIsPolling(false);
    setIsLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !isEmailValid) {
      updateEmailError('Please enter a valid email address');
      return;
    }

    if (!sessionId) {
      setLoginError('Session not initialized. Please refresh the page and try again.');
      captureEvent('login_failed_no_session', { email });
      return;
    }

    // Clear all previous states before starting
    setLoginError('');
    setVerificationStatus('');
    setIsLoading(true);

    try {
      // Update session with email (this will send verification email)
      const result = await dispatch(updateSessionEmail({ sessionId, email }));

      if (updateSessionEmail.fulfilled.match(result)) {
        console.log('Session updated with email:', result.payload);
        
        // Initiate verification (POST call - once)
        const verificationInitiated = await initiateVerification(email);
        
        if (verificationInitiated) {
          // Clear loading state before starting polling
          setIsLoading(false);
          setVerificationStatus('Verification email sent. Please check your inbox.');
          // Start polling for verification status (GET calls)
          startPolling();
        } else {
          setIsLoading(false);
          setLoginError('Failed to initiate verification. Please try again.');
        }
        
        // Don't close modal yet - wait for verification
      } else if (updateSessionEmail.rejected.match(result)) {
        setLoginError(result.payload?.message || 'Failed to send verification email. Please try again.');
        setIsLoading(false);
      }
    } catch (error) {
      setLoginError('An unexpected error occurred. Please try again.');
      console.error('Login error:', error);
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    // Stop polling if active
    stopPolling();
    
    // Reset form state
    updateEmail('');
    updateEmailError('');
    updateIsEmailValid(false);
    setLoginError('');
    setVerificationStatus('');
    setIsLoading(false);
    onClose();
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);

  if (!isOpen) return null;

  return (
    <div className="modal fade show d-block" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 1000, backdropFilter: 'blur(4px)' }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content border-0 rounded-4" style={{ background: 'white', borderRadius: 12, width: '100%', maxWidth: 400, margin: '0 auto' }}>
          <div className="d-flex justify-content-between align-items-center p-4 pb-0" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
            <h2 className="h4 mb-0" style={{ margin: 0, fontSize: 24, fontWeight: 600, color: '#111827' }}>Login to Ankr</h2>
            <button 
              className="btn btn-link p-0 rounded-2" 
              style={{ 
                background: 'none', 
                border: 'none', 
                fontSize: 24, 
                color: '#6b7280', 
                cursor: 'pointer', 
                padding: 8, 
                transition: 'all 0.2s', 
                borderRadius: 8 
              }}
              onClick={handleClose}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#f3f4f6';
                e.target.style.color = '#111827';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#6b7280';
              }}
            >
              ×
            </button>
          </div>
          
          <div className="px-4 pb-4" style={{ padding: '0 24px 24px 24px' }}>
            <form onSubmit={handleSubmit} className="d-flex flex-column gap-4" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div className="mb-3" style={{ marginBottom: 16 }}>
                <label htmlFor="email" className="form-label fw-semibold" style={{ 
                  display: 'block', 
                  marginBottom: 8, 
                  fontSize: 14, 
                  fontWeight: 500, 
                  color: '#374151' 
                }}>Email Address</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={handleEmailChange}
                  placeholder="Enter your email address"
                  className={`form-control ${emailError ? 'is-invalid' : ''}`}
                  disabled={isLoading}
                  style={{ 
                    padding: '12px 16px', 
                    border: emailError ? '1px solid #ef4444' : '1px solid #d1d5db', 
                    borderRadius: 8, 
                    fontSize: 16, 
                    color: '#1f2937', 
                    outline: 'none', 
                    transition: 'all 0.2s' 
                  }}
                />
                {emailError && (
                  <div className="invalid-feedback d-block" style={{ 
                    fontSize: 14, 
                    color: '#ef4444', 
                    marginTop: 4 
                  }}>{emailError}</div>
                )}
              </div>

              {loginError && (
                <div className="alert alert-danger py-2 px-3" style={{ 
                  backgroundColor: '#fef2f2', 
                  border: '1px solid #fecaca', 
                  borderRadius: 8, 
                  padding: '12px 16px', 
                  fontSize: 14, 
                  color: '#dc2626' 
                }}>
                  {loginError}
                </div>
              )}

              {isPolling && (
                <div className="d-flex align-items-center gap-2 p-3 bg-light rounded-2" style={{ 
                  backgroundColor: '#eff6ff', 
                  border: '1px solid #bfdbfe', 
                  borderRadius: 8, 
                  padding: '12px 16px', 
                  fontSize: 14, 
                  color: '#1e40af', 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 8 
                }}>
                  <div className="spinner-border spinner-border-sm" role="status" style={{ 
                    width: 16, 
                    height: 16, 
                    border: '2px solid #e5e7eb', 
                    borderTopColor: '#3b82f6', 
                    borderRadius: '50%', 
                    animation: 'spin 1s linear infinite' 
                  }}>
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <span>{verificationStatus}</span>
                </div>
              )}

              <button
                type="submit"
                className="btn btn-primary w-100"
                disabled={!isEmailValid || isLoading || isPolling}
                style={{ 
                  backgroundColor: (!isEmailValid || isLoading || isPolling) ? '#9ca3af' : '#3b82f6', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: 8, 
                  padding: '12px 16px', 
                  fontSize: 16, 
                  fontWeight: 500, 
                  cursor: (!isEmailValid || isLoading || isPolling) ? 'not-allowed' : 'pointer', 
                  transition: 'all 0.2s', 
                  marginTop: 8 
                }}
                onMouseEnter={(e) => {
                  if (isEmailValid && !isLoading && !isPolling) {
                    e.target.style.backgroundColor = '#2563eb';
                  }
                }}
                onMouseLeave={(e) => {
                  if (isEmailValid && !isLoading && !isPolling) {
                    e.target.style.backgroundColor = '#3b82f6';
                  }
                }}
              >
                {isLoading ? 'Sending...' : isPolling ? 'Checking Email...' : 'Send Verification Email'}
              </button>
            </form>

            <div className="mt-4 pt-4 border-top" style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid #e5e7eb' }}>
              <p className="text-muted mb-0" style={{ 
                margin: 0, 
                color: '#6b7280', 
                fontSize: 14, 
                lineHeight: 1.5 
              }}>
                We&apos;ll send you a verification email to securely log you in.
                Click the link in the email to complete your login.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginModal;
